/**
 * Module-level OTA status store, shared between the launch-time check (which
 * runs in the root layout) and any UI that displays update progress (the Profile
 * "About / Updates" section and the global update sheet). Exposed via
 * `useSyncExternalStore` so React components re-render on stage changes.
 */
import { useSyncExternalStore } from 'react';

export type OtaStage =
  | 'idle'
  | 'checking'
  | 'found'
  | 'downloading'
  | 'ready'
  | 'up-to-date'
  | 'error';

export type OtaStatus = {
  stage: OtaStage;
  version?: string;
  error?: string;
};

let state: OtaStatus = { stage: 'idle' };
const listeners = new Set<() => void>();

export function setOtaStatus(stage: OtaStage, extra?: Omit<OtaStatus, 'stage'>): void {
  state = { stage, ...extra };
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): OtaStatus {
  return state;
}

export function useOtaStatus(): OtaStatus {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
